import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, PageSizeType, Table } from 'design-system-web';
import { Alert } from 'components/lds';

import { buildCoverageHoursRowActions } from './coverageHoursRowActions';
import {
  CoverageHoursSummary,
  createCoverageHoursRowViewModel,
  formatCoverageHours,
} from './coverageHoursRowFormat';
import {
  coverageHoursRowId,
  coverageHoursSubRows,
  CoverageHoursTableRow,
  isCoverageHoursRouting,
} from './coverageHoursTableRows';
import {
  CoverageHoursQueryError,
  CoverageHoursRouting,
} from './useCoverageHours';
import styles from './CoverageHours.scss';

type Props = {
  routings: CoverageHoursRouting[];
  totalRows: number;
  loading: boolean;
  error?: CoverageHoursQueryError;
  canManage: boolean;
  page: number;
  pageSize: PageSizeType;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: PageSizeType) => void;
  onDeleteRow: (routing: CoverageHoursRouting) => void;
  isDeleteDisabled: boolean;
};

const countChip = (label: string) => (
  <span className={styles.countChip}>{label}</span>
);

const summaryCell = ({ label, isCount }: CoverageHoursSummary) =>
  isCount ? countChip(label) : label;

export function CoverageHoursTable({
  routings,
  totalRows,
  loading,
  error,
  canManage,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onDeleteRow,
  isDeleteDisabled,
}: Props) {
  const { t } = useTranslation();

  const columns: Column<CoverageHoursTableRow>[] = useMemo(
    () => [
      {
        key: 'categoryIssue',
        header: t(
          'web.admin.serviceRequest.settings.coverageHours.table.categoryIssue'
        ),
        renderCell: (_cell: unknown, row: CoverageHoursTableRow) =>
          isCoverageHoursRouting(row)
            ? createCoverageHoursRowViewModel(row, t).categoryIssue
            : null,
      },
      {
        key: 'team',
        header: t('web.admin.serviceRequest.settings.coverageHours.table.team'),
        renderCell: (_cell: unknown, row: CoverageHoursTableRow) =>
          isCoverageHoursRouting(row)
            ? summaryCell(createCoverageHoursRowViewModel(row, t).team)
            : (row.teamName ??
              t(
                'web.admin.serviceRequest.settings.coverageHours.table.teamUnavailable'
              )),
      },
      {
        key: 'coverageHours',
        header: t(
          'web.admin.serviceRequest.settings.coverageHours.table.coverageHours'
        ),
        renderCell: (_cell: unknown, row: CoverageHoursTableRow) =>
          isCoverageHoursRouting(row)
            ? summaryCell(createCoverageHoursRowViewModel(row, t).coverage)
            : formatCoverageHours(row),
      },
    ],
    [t]
  );

  const rowActions = useMemo(
    () =>
      canManage
        ? buildCoverageHoursRowActions(t, onDeleteRow, isDeleteDisabled)
        : undefined,
    [canManage, t, onDeleteRow, isDeleteDisabled]
  );

  if (error) {
    return (
      <Alert type="error">
        {t('web.admin.serviceRequest.settings.coverageHours.error.load')}
      </Alert>
    );
  }

  return (
    <Table<CoverageHoursTableRow>
      columns={columns}
      data={routings}
      getSubRows={coverageHoursSubRows}
      rowActions={rowActions}
      disableSorting
      isLoading={loading}
      getRowId={coverageHoursRowId}
      pagination="server"
      totalRows={totalRows}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      showColumnVisibility
      tableKey="serviceRequestSettings.coverageHoursTable"
      emptyMessage={t(
        canManage
          ? 'web.admin.serviceRequest.settings.coverageHours.empty'
          : 'web.admin.serviceRequest.settings.coverageHours.emptyReadOnly'
      )}
    />
  );
}
